import React from 'react';
import {
  useAnalyticsRealtime,
  useAuditLogsRealtime,
  useSystemHealthRealtime,
} from '../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MetricCard } from '../components/ui/MetricCard';
import { Badge } from '../components/ui/Badge';
import { cn } from '../utils/cn';
import { getCostAnalyticsByModel } from '../utils/adminApi';
import { useAuth } from '../contexts/AuthContext';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { idToken } = useAuth();

  const {
    data: analyticsResponse,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useAnalyticsRealtime(undefined, true, 5 * 60 * 1000);
  const { data: auditLogsResponse, isLoading: auditLogsLoading } =
    useAuditLogsRealtime({ limit: 10 }, true);
  const {
    data: systemHealthResponse,
    isLoading: systemHealthLoading,
    refetch: refetchSystemHealth,
  } = useSystemHealthRealtime(true, 60 * 1000);

  // Cost widget summary: same adminApi call as the Cost Monitoring page.
  const { data: costsResponse } = useQuery({
    queryKey: ['analytics', 'costs'],
    queryFn: () =>
      idToken ? getCostAnalyticsByModel(idToken) : Promise.resolve(null),
    enabled: !!idToken,
  });

  const analytics = (analyticsResponse as any) || null;
  const recentLogs = (auditLogsResponse as any)?.logs || [];
  const systemHealth = (systemHealthResponse as any) || null;
  const costs = (costsResponse as any) || null;

  const loading = analyticsLoading || auditLogsLoading || systemHealthLoading;

  const handleRefresh = async () => {
    await Promise.all([refetchAnalytics(), refetchSystemHealth()]);
  };

  const getActionColor = (action: string) => {
    if (action.includes('delete') || action.includes('revoke'))
      return 'bg-rose-500 shadow-rose-500/50';
    if (action.includes('create') || action.includes('grant'))
      return 'bg-emerald-500 shadow-emerald-500/50';
    if (action.includes('update') || action.includes('adjust'))
      return 'bg-amber-500 shadow-amber-500/50';
    return 'bg-blue-500 shadow-blue-500/50';
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown time';
    let date = new Date(
      timestamp._seconds ? timestamp._seconds * 1000 : timestamp
    );
    const diffMins = Math.floor(
      (new Date().getTime() - date.getTime()) / 60000
    );
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-20 bg-slate-800/50 rounded-2xl w-full"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="h-32 bg-slate-800/50 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Area */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
            Overview
          </h1>
          <p className="text-slate-400 mt-1">
            Platform metrics and system health
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {systemHealth && (
            <Badge
              variant={
                systemHealth.overall === 'healthy'
                  ? 'success'
                  : systemHealth.overall === 'degraded'
                    ? 'warning'
                    : 'critical'
              }
              className="px-3 py-1 text-sm shadow-xl backdrop-blur-md"
            >
              System {systemHealth.overall}
            </Badge>
          )}

          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 border border-white/10 rounded-xl transition-all shadow-lg text-slate-300 backdrop-blur-md"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {costs?.totals?.callsWithoutCost > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 backdrop-blur-md rounded-2xl p-4 flex items-start shadow-xl shadow-amber-500/5">
          <div className="p-2 bg-amber-500/20 rounded-lg shrink-0">
            <svg
              className="w-5 h-5 text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-bold text-amber-400">
              Pricing Configuration Warning
            </h3>
            <p className="text-amber-200/80 text-sm mt-1">
              There are {costs.totals.callsWithoutCost} generations that costed
              $0 because they are missing from the Model Registry pricing rules.
            </p>
            <button
              onClick={() => navigate('/cost-monitoring')}
              className="text-amber-300 hover:text-amber-100 text-xs font-semibold mt-2 underline"
            >
              View Details
            </button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={analytics?.users?.total || 0}
          change={{
            value: analytics?.users?.growth?.monthly || 0,
            type:
              (analytics?.users?.growth?.monthly || 0) >= 0
                ? 'increase'
                : 'decrease',
            period: '30d',
          }}
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
          }
        />
        <MetricCard
          title="Active Orgs"
          value={analytics?.organizations?.total || 0}
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
        />
        <MetricCard
          title="Generations Today"
          value={analytics?.usage?.generationsToday || 0}
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          }
        />
        <MetricCard
          title="Credits Used (30d)"
          value={analytics?.credits?.totalUsed || 0}
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              />
            </svg>
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Monitoring Summary */}
        <div className="bg-slate-900/40 border border-white/5 backdrop-blur-xl rounded-3xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">
              Cost & Margin Summary
            </h3>
            <button
              onClick={() => navigate('/cost-monitoring')}
              className="text-sm font-medium text-blue-400 hover:text-blue-300 bg-blue-500/10 px-3 py-1 rounded-full transition-colors"
            >
              Details →
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-2xl p-4 border border-white/5">
              <p className="text-sm text-slate-400 mb-1">
                Estimated Provider Costs
              </p>
              <p className="text-2xl font-bold text-white">
                {costs?.totals?.providerCostUsd || '$0.00'}
              </p>
            </div>
            <div className="bg-emerald-500/10 rounded-2xl p-4 border border-emerald-500/20">
              <p className="text-sm text-emerald-400/80 mb-1">Approx Margin</p>
              <p className="text-2xl font-bold text-emerald-400">
                {costs?.totals?.marginRatio != null
                  ? (costs.totals.marginRatio * 100).toFixed(1)
                  : '0'}
                %
              </p>
            </div>
            <div className="col-span-2 bg-slate-800/30 rounded-2xl p-4 border border-white/5 mt-2 flex justify-between items-center">
              <span className="text-slate-400">Total API Calls</span>
              <span className="text-lg font-bold text-white">
                {costs?.totals?.calls || 0}
              </span>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-slate-900/40 border border-white/5 backdrop-blur-xl rounded-3xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">System Health</h3>
            <button
              onClick={() => navigate('/system-health')}
              className="text-sm font-medium text-blue-400 hover:text-blue-300 bg-blue-500/10 px-3 py-1 rounded-full transition-colors"
            >
              Details →
            </button>
          </div>
          {systemHealth ? (
            <div className="space-y-4">
              {systemHealth.services.slice(0, 4).map((service: any) => (
                <div
                  key={service.service}
                  className="flex justify-between items-center group"
                >
                  <span className="text-slate-300 font-medium group-hover:text-white transition-colors">
                    {service.service}
                  </span>
                  <div className="flex items-center space-x-3">
                    {service.responseTime && (
                      <span className="text-xs text-slate-500 font-mono">
                        {service.responseTime}ms
                      </span>
                    )}
                    <span className="text-xs text-slate-400">
                      {service.version || 'v1.0.0'}
                    </span>
                    <div
                      className={cn(
                        'w-2.5 h-2.5 rounded-full shadow-[0_0_8px]',
                        service.status === 'healthy'
                          ? 'bg-emerald-400 shadow-emerald-400/50'
                          : service.status === 'unhealthy'
                            ? 'bg-amber-400 shadow-amber-400/50'
                            : 'bg-rose-400 shadow-rose-400/50'
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">Unavailable</div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-slate-900/40 border border-white/5 backdrop-blur-xl rounded-3xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Audit Logs</h3>
            <button
              onClick={() => navigate('/audit-logs')}
              className="text-sm font-medium text-blue-400 hover:text-blue-300 bg-blue-500/10 px-3 py-1 rounded-full transition-colors"
            >
              View All →
            </button>
          </div>
          <div className="space-y-4">
            {recentLogs.length === 0 ? (
              <p className="text-slate-500">No recent activity.</p>
            ) : (
              recentLogs.slice(0, 5).map((log: any) => (
                <div
                  key={log.id}
                  className="flex items-start space-x-4 bg-slate-800/30 p-4 rounded-2xl border border-white/5 hover:bg-slate-800/50 transition-colors"
                >
                  <div
                    className={cn(
                      'w-2.5 h-2.5 rounded-full mt-1.5 shadow-[0_0_8px]',
                      getActionColor(log.action)
                    )}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-white capitalize">
                      {log.action.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      <span className="text-blue-400">
                        {log.actor?.email || log.adminEmail || 'System'}
                      </span>{' '}
                      • {formatTimestamp(log.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
